import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import api from "@/lib/api";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import { ScrollText, ShieldCheck, Loader2 } from "lucide-react";

const ENTITY_FILTERS = [
  { key: "all", label: "All" },
  { key: "service_module", label: "Modules" },
  { key: "state", label: "States" },
  { key: "restaurant", label: "Restaurants" },
  { key: "business", label: "Businesses" },
];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const fmtDiff = (diff) => {
  if (!diff || typeof diff !== "object" || Object.keys(diff).length === 0) return "—";
  try { return JSON.stringify(diff); } catch (_) { return "—"; }
};

export default function AdminAuditLogs() {
  const { t } = useLang();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const params = filter === "all" ? { limit: 200 } : { entity_type: filter, limit: 200 };
    api.get("/admin/audit-logs", { params })
      .then((r) => { if (active) setLogs(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || "Failed to load audit logs"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filter]);

  return (
    <DashboardLayout items={adminNavItems(t)} title="Audit Logs">
      <div className="mb-6 max-w-2xl flex items-center gap-3" data-testid="audit-header">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--so-orange)]/10 text-[var(--so-orange)]">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <p className="text-[var(--so-text-soft)] text-sm">
          Every sensitive admin action (module toggles, state changes, business creation, feature flags) is recorded here for security and accountability.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="audit-filters">
        {ENTITY_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`audit-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
              filter === f.key
                ? "bg-[var(--so-orange)] text-white border-[var(--so-orange)]"
                : "bg-[var(--so-surface)] text-[var(--so-text-soft)] border-[var(--so-border)] hover:text-[var(--so-text)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="so-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--so-border)] font-bold flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-[var(--so-text-soft)]" />
          Activity trail
          <span className="text-[var(--so-text-soft)] font-normal text-sm">({logs.length})</span>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-[var(--so-text-soft)]" data-testid="audit-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500 text-sm" data-testid="audit-error">{error}</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-[var(--so-text-soft)] text-sm" data-testid="audit-empty">No audit records for this filter yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--so-bg)] text-xs uppercase tracking-wider text-[var(--so-text-soft)]">
                <tr>
                  <th className="text-start p-3">When</th>
                  <th className="text-start p-3">Actor</th>
                  <th className="text-start p-3">Action</th>
                  <th className="text-start p-3">Entity</th>
                  <th className="text-start p-3">Changes</th>
                  <th className="text-start p-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log?.id || i} className="border-t border-[var(--so-border)] align-top" data-testid={`audit-row-${log?.id || i}`}>
                    <td className="p-3 text-[var(--so-text-soft)] text-xs whitespace-nowrap">{fmtDate(log?.created_at)}</td>
                    <td className="p-3">
                      <div className="font-semibold">{log?.actor_email || "system"}</div>
                      <div className="text-[11px] text-[var(--so-text-soft)]">{log?.actor_role || "—"}</div>
                    </td>
                    <td className="p-3">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[var(--so-orange)]/10 text-[var(--so-orange)]">
                        {log?.action || "—"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{log?.entity_type || "—"}</div>
                      <div className="text-[11px] text-[var(--so-text-soft)] font-mono break-all">{log?.entity_id || ""}</div>
                    </td>
                    <td className="p-3 text-[var(--so-text-soft)] text-xs font-mono max-w-[260px] break-all">{fmtDiff(log?.diff)}</td>
                    <td className="p-3 text-[var(--so-text-soft)] text-xs">{log?.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
